import {
  Button,
  Col,
  Menu,
  Row,
  SearchField,
  TinyTable,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { cn } from '@rematter/ui-web/lib';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import {
  LucideCheck,
  LucideEllipsis,
  LucideFileBarChart,
  LucideGlobe,
  LucidePlusCircle,
  LucideSignpost,
  LucideSquarePen,
  LucideTrash,
  LucideUpload,
} from 'lucide-react';

import { GovReportingConfigRecord } from '../../_graphql/queryGovReportingConfigs';
import * as govReportingState from '../../_state/govReporting.state';
import { useSettingsLocation } from '../../_state/settingsModule.state';
import { useTranslations } from '../../i18n/useTranslations';
import { SettingsSubmodule } from '../_layouts/SettingsSubmodule';
import { SettingsContent } from '../_shared/SettingsContent';

import { GovReportingConfigFormDialog } from './GovReportingConfigFormDialog';

type ConfigRowMenuProps = { row: GovReportingConfigRecord };

const ConfigRowMenu = (props: ConfigRowMenuProps) => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const setEditingConfig = useSetAtom(
    govReportingState.$editingGovReportingConfig,
  );
  const setDialogOpen = useSetAtom(govReportingState.$isGovReportingDialogOpen);
  const deleteConfig = useSetAtom(govReportingState.$deleteGovReportingConfig);
  const toast = useToast();

  const handleDelete = async () => {
    try {
      await deleteConfig({ id: props.row.id });
      toast.success(
        t.complianceView.govReporting.dialog.messages.deleteSuccess,
      );
    } catch (error) {
      toast.error(
        t.complianceView.govReporting.dialog.messages.deleteError(
          error instanceof Error ? error.message : 'Unknown error',
        ),
      );
    }
  };

  return (
    <div className="@ConfigRowMenu contents">
      <Menu
        trigger={
          <Button
            className="px-2"
            size="small"
            color="neutral"
            variant="outline"
            icon={<LucideEllipsis />}
          />
        }
        items={[
          {
            label: t.complianceView.common.actions.viewDetails,
            icon: <LucideFileBarChart />,
            onAction: () =>
              setLocation(`/compliance/reporting/${props.row.id}`),
          },
          {
            label: t.complianceView.common.actions.edit,
            icon: <LucideSquarePen />,
            onAction: () => {
              setEditingConfig(props.row.id);
              setDialogOpen(true);
            },
          },
          {
            label: t.complianceView.common.actions.delete,
            icon: <LucideTrash />,
            className: 'text-failure',
            secondStep: ({ close }) => (
              <Col gap3>
                <div className="text-center text-sm text-neutral/70">
                  {t.complianceView.common.confirmDelete}
                </div>
                <Row gap2>
                  <Button variant="outline" color="neutral" onPress={close}>
                    {t.complianceView.common.actions.cancel}
                  </Button>
                  <Button
                    variant="outline"
                    color="failure"
                    icon={<LucideTrash />}
                    onPress={() => {
                      close();
                      handleDelete();
                    }}
                  >
                    {t.complianceView.common.yesDelete}
                  </Button>
                </Row>
              </Col>
            ),
          },
        ]}
      />
    </div>
  );
};

export const GovReportingConfigView = () => {
  const t = useTranslations();
  const [, setLocation] = useSettingsLocation();
  const configs = useAtomValue(govReportingState.$govReportingConfigs);
  const [searchTerm, setSearchTerm] = useAtom(
    govReportingState.$govReportingSearchTerm.value,
  );
  const setDialogOpen = useSetAtom(govReportingState.$isGovReportingDialogOpen);
  const setEditingConfig = useSetAtom(
    govReportingState.$editingGovReportingConfig,
  );

  return (
    <Col className="@GovReportingConfigView h-full">
      <SettingsSubmodule
        title={t.complianceView.govReporting.title}
        subtitle={t.complianceView.govReporting.subtitle}
        tabs={null}
      >
        <SettingsContent className="h-full">
          <Col gap4 className="h-full">
            <Row gap2>
              <SearchField
                value={searchTerm}
                onChange={setSearchTerm}
                autoFocus
                placeholder={t.complianceView.govReporting.search.placeholder}
              />
              <Button
                className="ml-auto"
                icon={<LucidePlusCircle />}
                onPress={() => {
                  setEditingConfig(null);
                  setDialogOpen(true);
                }}
              >
                {t.complianceView.govReporting.actions.addConfig}
              </Button>
            </Row>
            <GovReportingConfigFormDialog />
            <TinyTable
              rowKey={(row) => row.id}
              isLoading={configs.isLoading}
              error={configs.error}
              onRowClick={(row) =>
                setLocation(`/compliance/reporting/${row.id}`)
              }
              columns={[
                {
                  key: 'name',
                  label: t.complianceView.govReporting.columns.name,
                  icon: LucideSignpost,
                  cell: (row) => (
                    <div className="truncate font-medium">{row.name}</div>
                  ),
                },
                {
                  key: 'serviceType',
                  label: t.complianceView.govReporting.columns.serviceType,
                  icon: LucideGlobe,
                  width: 200,
                  cell: (row) => (
                    <div className="truncate">{row.serviceType}</div>
                  ),
                },
                {
                  key: 'uploadType',
                  label: t.complianceView.govReporting.columns.uploadType,
                  icon: LucideUpload,
                  width: 120,
                  cell: (row) => row.uploadType,
                },
                {
                  key: 'active',
                  label: t.complianceView.govReporting.columns.active,
                  icon: LucideCheck,
                  width: 80,
                  cell: (row) => (
                    <span
                      className={cn(
                        row.isActive && 'text-success',
                        !row.isActive && 'text-neutral/40',
                      )}
                    >
                      {row.isActive
                        ? t.complianceView.common.yes
                        : t.complianceView.common.no}
                    </span>
                  ),
                },
                {
                  key: 'actions',
                  label: null,
                  preventRowClick: true,
                  width: 'max-content',
                  cell: (row) => <ConfigRowMenu row={row} />,
                },
              ]}
              data={configs.data ?? []}
            />
          </Col>
        </SettingsContent>
      </SettingsSubmodule>
    </Col>
  );
};
